import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import api from "../../lib/api";
import { toast } from "sonner";
import { ArrowRight, RefreshCw, Clock, CheckCircle2, FileText, Plus } from "lucide-react";

export default function AdminReconciliation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attribOpen, setAttribOpen] = useState(false);
  const [activeSettlement, setActiveSettlement] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState({});
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ amount_stablecoin: "", currency: "USDC" });

  const load = () => {
    setLoading(true);
    api.get("/admin/reconciliation/worklist")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAttribute = (s) => {
    setActiveSettlement(s);
    setSelectedOrders({});
    setAttribOpen(true);
  };

  const submitAttribute = async () => {
    const order_ids = Object.entries(selectedOrders).filter(([, v]) => v).map(([k]) => k);
    if (order_ids.length === 0) { toast.error("Pick at least one order"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/reconciliation/attribute", {
        settlement_id: activeSettlement.xgate_transaction_id,
        order_ids,
        notes: `manual reconcile by operator`,
      });
      toast.success(`Attributed ${r.data.matched.length} order(s)`);
      if (r.data.missing?.length) toast.warning(`${r.data.missing.length} not matched (already posted?)`);
      setAttribOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const createSettlement = async () => {
    if (!draft.amount_stablecoin) return;
    try {
      await api.post("/admin/reconciliation/settlements", {
        amount_stablecoin: parseFloat(draft.amount_stablecoin),
        currency: draft.currency,
      });
      toast.success("Settlement logged");
      setCreateOpen(false);
      setDraft({ amount_stablecoin: "", currency: "USDC" });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <AdminLayout
      title="Reconciliation"
      subtitle="Three-leg attribution: match XGate settlement batches to fronted-out orders."
      actions={
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" className="rounded-xl gap-1.5" data-testid="recon-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
          <Button onClick={() => setCreateOpen(true)} size="sm"
                  className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                  data-testid="recon-log-batch-btn">
            <Plus className="w-3.5 h-3.5" /> Log batch
          </Button>
        </div>
      }
    >
      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="recon-summary">
            <SummaryCard label="Unmatched batches"   value={data.summary.unmatched_settlements_count} testid="sum-unmatched" />
            <SummaryCard label="Pending crypto_in"   value={data.summary.pending_crypto_in_count} testid="sum-pending" />
            <SummaryCard label="USDC pending"        value={(data.summary.pending_crypto_in_total_usdc || 0).toLocaleString("en-IE", { maximumFractionDigits: 2 })} suffix="USDC" testid="sum-usdc" />
            <SummaryCard label="USDT pending"        value={(data.summary.pending_crypto_in_total_usdt || 0).toLocaleString("en-IE", { maximumFractionDigits: 2 })} suffix="USDT" testid="sum-usdt" />
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            {/* Unmatched settlements */}
            <div className="xch-card overflow-hidden" data-testid="recon-settlements">
              <div className="p-4 border-b border-border font-display text-base font-bold flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" /> XGate settlement batches
              </div>
              {data.unmatched_settlements.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground" data-testid="settlements-empty">No unmatched batches.</div>
              ) : (
                <div className="divide-y divide-border">
                  {data.unmatched_settlements.map((s, i) => (
                    <div key={s.xgate_transaction_id || i} className="p-3 flex items-center gap-3" data-testid={`settlement-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-xs text-muted-foreground truncate">{s.xgate_transaction_id}</div>
                        <div className="font-display font-bold text-foreground">
                          {(s.amount_stablecoin || 0).toLocaleString("en-IE", { maximumFractionDigits: 2 })} {s.currency}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          Received {new Date(s.received_at).toLocaleString("en-IE")}
                          {s.source === "manual" && <Badge variant="outline" className="ml-2 text-[9px]">manual</Badge>}
                        </div>
                      </div>
                      <Button onClick={() => openAttribute(s)} size="sm"
                              className="bg-primary hover:bg-primary/90 text-white rounded-lg gap-1"
                              data-testid={`attribute-${i}`}>
                        Attribute <ArrowRight className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pending crypto_in by order */}
            <div className="xch-card overflow-hidden" data-testid="recon-pending">
              <div className="p-4 border-b border-border font-display text-base font-bold flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-600" /> Pending crypto_in (T+1)
              </div>
              {data.pending_crypto_in.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground" data-testid="pending-empty">No pending entries.</div>
              ) : (
                <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
                  {data.pending_crypto_in.map((row, i) => (
                    <div key={i} className="p-3" data-testid={`pending-row-${i}`}>
                      <div className="font-mono text-[11px] text-muted-foreground">{row.order?.reference}</div>
                      <div className="text-sm font-semibold text-foreground">
                        €{row.order?.fiat_amount} → {row.entries.map(e => `${e.amount.toFixed(2)} ${e.currency}`).join(", ")}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <Dialog open={attribOpen} onOpenChange={setAttribOpen}>
        <DialogContent className="max-w-lg rounded-3xl" data-testid="attribute-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Attribute batch to orders</DialogTitle>
            <DialogDescription className="text-xs">
              Match this XGate batch to one or more customer orders to clear the unmatched balance.
            </DialogDescription>
          </DialogHeader>
          {activeSettlement && (
            <div className="space-y-3">
              <div className="bg-muted/40 rounded-xl p-3">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Batch</div>
                <div className="font-mono text-xs">{activeSettlement.xgate_transaction_id}</div>
                <div className="font-display font-bold text-foreground">
                  {(activeSettlement.amount_stablecoin || 0).toLocaleString("en-IE", { maximumFractionDigits: 2 })} {activeSettlement.currency}
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Select orders this batch reimburses</div>
              <div className="max-h-72 overflow-y-auto space-y-1 border border-border rounded-xl p-2">
                {(data?.pending_crypto_in || [])
                  .filter(r => r.entries.some(e => e.currency === activeSettlement.currency))
                  .map((row, i) => (
                  <label key={row.order?.id || i}
                         className="flex items-center gap-2 p-2 hover:bg-muted/40 rounded-lg cursor-pointer"
                         data-testid={`pick-${row.order?.id}`}>
                    <Checkbox
                      checked={!!selectedOrders[row.order?.id]}
                      onCheckedChange={(v) => setSelectedOrders({ ...selectedOrders, [row.order.id]: !!v })}
                    />
                    <div className="flex-1 min-w-0 text-sm">
                      <div className="font-mono text-[11px] text-muted-foreground">{row.order?.reference}</div>
                      <div className="font-semibold text-foreground">€{row.order?.fiat_amount} ·
                        {row.entries.filter(e => e.currency === activeSettlement.currency).map(e => ` ${e.amount.toFixed(2)} ${e.currency}`).join("")}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAttribOpen(false)}>Cancel</Button>
            <Button onClick={submitAttribute} disabled={busy}
                    className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1"
                    data-testid="confirm-attribute-btn">
              <CheckCircle2 className="w-4 h-4" /> Attribute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="create-settlement-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Log a settlement batch</DialogTitle>
            <DialogDescription className="text-xs">
              Record a manual XGate settlement batch (T+1 stablecoin receipt) for downstream attribution.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Amount</Label>
              <Input type="number" step="0.01" value={draft.amount_stablecoin}
                     onChange={(e) => setDraft({ ...draft, amount_stablecoin: e.target.value })}
                     className="rounded-xl font-mono" data-testid="batch-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Currency</Label>
              <Select value={draft.currency} onValueChange={(v) => setDraft({ ...draft, currency: v })}>
                <SelectTrigger className="rounded-xl" data-testid="batch-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USDC">USDC</SelectItem>
                  <SelectItem value="USDT">USDT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createSettlement}
                    className="bg-primary hover:bg-primary/90 text-white rounded-xl"
                    data-testid="confirm-batch-btn">Log batch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

const SummaryCard = ({ label, value, suffix, testid }) => (
  <div className="xch-card p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
    <div className="font-display text-2xl font-extrabold text-foreground tabular-nums">
      {value}{suffix && <span className="text-xs font-semibold text-muted-foreground ml-1">{suffix}</span>}
    </div>
  </div>
);
